/**
 * Giving a unit a new route without losing the step it's on. A unit is drawn
 * at `pos + progress·(path[0] − pos)`, so dropping `progress` (or `path[0]`)
 * mid-step snaps it back toward `pos`, up to a whole tile, and re-ordering it
 * faster than a step takes pins it in place. Every system that changes a
 * unit's path goes through `redirect`, which keeps the drawn point where it is:
 * - the new route's first step is the one in flight (or the route passes
 *   through it): the unit keeps its progress;
 * - past halfway, and the new route's first step is next to the one in flight:
 *   it finishes the step and turns there;
 * - otherwise it reverses the step: `pos` and `path[0]` swap and progress
 *   becomes `1 − p`, which draws the same point, then it walks back and on.
 * A stop (`[unit.pos]`) settles on the nearer end of the step the same way.
 * A blocked step (`Unit.blocked`) is the exception: a rival holds its tile, so
 * it can't be finished, and reversing would stand the unit on that tile. A
 * stop holds the unit where it waits on the step; a new way drops the step
 * (the unit snaps back to `pos`, as before).
 */
import { axialHexDistance } from '../hexmap/coords';
import { invalidateDerivedAfterSystem } from './derivedRegistry';
import { MILLI } from './fixed';
import type { Cell, SimState, Unit } from './types';

function same(a: Cell, b: Cell): boolean {
  return a.q === b.q && a.r === b.r;
}

/**
 * Sets `unit` walking `route` (`[start, …]`, `start` its `pos` or the tile
 * it's stepping onto; `[unit.pos]` stops it), keeping the step in flight (see
 * above). A unit given somewhere to go leaves the building it's inside.
 * Returns where it ends up (a stop may still finish its step).
 */
function redirect(state: SimState, unit: Unit, route: readonly Cell[]): Cell {
  steer(state, unit, route);
  return unit.path?.[unit.path.length - 1] ?? unit.pos;
}

function steer(state: SimState, unit: Unit, route: readonly Cell[]): void {
  const rest = route.slice(1).map((c) => ({ q: c.q, r: c.r }));
  const next = unit.path?.[0];
  const p = unit.progress;
  const from = route[0];
  if (!next || !from) {
    place(unit, rest, false);
    return;
  }
  if (same(from, next)) {
    // Planned from the tile it's stepping onto: finish the step, unless the route turns straight back.
    if (p > 0 && rest.length > 0 && same(rest[0], unit.pos)) {
      if (unit.blocked) place(unit, rest.slice(1), false);
      else reverse(state, unit, rest);
    } else place(unit, [{ q: next.q, r: next.r }, ...rest], true);
    return;
  }
  if (!same(from, unit.pos)) {
    // Not planned from either end of the step: nothing to keep.
    place(unit, rest, false);
    return;
  }
  const k = rest.findIndex((c) => same(c, next));
  if (p <= 0) {
    // Not under way yet: only a wait at an unchanged next step carries over.
    place(unit, rest, k === 0);
    return;
  }
  if (k >= 0) {
    place(unit, rest.slice(k), true);
    return;
  }
  if (unit.blocked) {
    // A stop holds it where it waits; a new way drops the step.
    if (rest.length === 0) place(unit, [{ q: next.q, r: next.r }], true);
    else place(unit, rest, false);
    return;
  }
  const finish = p * 2 >= MILLI && (rest.length === 0 || axialHexDistance(next, rest[0]) === 1);
  if (finish) place(unit, [{ q: next.q, r: next.r }, ...rest], true);
  else reverse(state, unit, [{ q: unit.pos.q, r: unit.pos.r }, ...rest]);
}

/** Sets `path` (next-first, may be empty); `keep` keeps the step in flight's progress and wait. */
function place(unit: Unit, path: Cell[], keep: boolean): void {
  if (!keep) {
    unit.progress = 0;
    delete unit.blocked;
  }
  if (path.length === 0) {
    delete unit.path;
    unit.progress = 0;
    return;
  }
  delete unit.inside;
  unit.path = path;
}

/** Turns the step in flight around: same drawn point, heading back to `pos`, then along `path`'s rest. */
function reverse(state: SimState, unit: Unit, path: Cell[]): void {
  const next = unit.path?.[0];
  if (!next) return;
  unit.pos = { q: next.q, r: next.r };
  unit.progress = MILLI - unit.progress;
  delete unit.blocked;
  unit.path = path;
  // It now stands on the other end of the step. Dropped once the system
  // ends, so the system still reads what it worked out (derived.ts).
  invalidateDerivedAfterSystem(state);
}

export { redirect };
